import React, { useState } from "react";
import { useEffect } from "react";
import { Camera, X, Home } from "lucide-react";
import { QRScanner } from "../../components/QRScanner";
import { CameraCapture } from "../../components/CameraCapture";
import { submitPenalty, loadLocations, getUserCurrentEventId, getUserRole, type PenaltySubmissionData } from "../../lib/api";
import { supabase } from "../../lib/supabase";
import type { Location } from "../../lib/supabase";
import { ProtectedRoute } from "../../components/ProtectedRoute";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Header } from "../../components/Header";

export function Artboard() {
  return (
    <ProtectedRoute>
      <ArtboardContent />
    </ProtectedRoute>
  );
}

function ArtboardContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [raceNumber, setRaceNumber] = useState("");
  const [numPenalties, setNumPenalties] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(() => {
    // Load saved location from session storage on initial load
    return sessionStorage.getItem('currentLocation') || "";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [athleteName, setAthleteName] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [currentEventId, setCurrentEventId] = useState<number | null>(null);
  const [raceoffroleid, setRaceoffroleid] = useState<number | null>(null);
  const [judgestation, setJudgestation] = useState<number | null>(null);
  const [showPenaltyTimeModal, setShowPenaltyTimeModal] = useState(false);
  const [penaltyTimeMultiplier, setPenaltyTimeMultiplier] = useState(1);
  
  // Dynamic data from database
  const [locations, setLocations] = useState<Location[]>([]);
  const [penaltyTypes, setPenaltyTypes] = useState<Array<{penaltyid: number, penaltytime: number}>>([]);
  const [isLoading, setIsLoading] = useState(true);

  /** Multiplier UI always steps in 15s units (×1=15, ×2=30, …). */
  const PENALTY_STEP_SECONDS = 15;

  const getDefaultMultiplierForLocation = (locationId: string, locs: Location[] = locations) => {
    const baseTime = locs.find((loc) => loc.locationid.toString() === locationId)?.penaltytime || 15;
    return Math.max(1, Math.round(baseTime / PENALTY_STEP_SECONDS));
  };

  // Handle location change with persistence
  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
    sessionStorage.setItem('currentLocation', value);
    setPenaltyTimeMultiplier(getDefaultMultiplierForLocation(value));
  };

  // Load data from database on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      const locationsResult = await loadLocations();
      
      // Load penalty types
      const { data: penaltyTypesData, error: penaltyTypesError } = await supabase
        .from('penaltype')
        .select('penaltyid, penaltytime')
        .order('penaltyid');

      if (locationsResult.success && locationsResult.data) {
        setLocations(locationsResult.data);
        
        if (penaltyTypesData && !penaltyTypesError) {
          setPenaltyTypes(penaltyTypesData);
        }
        
        // Handle initial location selection after locations are loaded
        const savedLocation = sessionStorage.getItem('currentLocation');
        if (savedLocation && locationsResult.data.some(loc => loc.locationid.toString() === savedLocation)) {
          // Use saved location if it exists in the loaded locations
          setSelectedLocation(savedLocation);
          setPenaltyTimeMultiplier(getDefaultMultiplierForLocation(savedLocation, locationsResult.data));
        } else {
          // Clear invalid saved location and use first available
          sessionStorage.removeItem('currentLocation');
          if (locationsResult.data.length > 0) {
            const firstLocationId = locationsResult.data[0].locationid.toString();
            setSelectedLocation(firstLocationId);
            sessionStorage.setItem('currentLocation', firstLocationId);
            setPenaltyTimeMultiplier(getDefaultMultiplierForLocation(firstLocationId, locationsResult.data));
          }
        }
      }

      // Load user's current event ID
      if (user?.email) {
        const eventResponse = await getUserCurrentEventId(user.email);
        if (eventResponse.success && eventResponse.data) {
          setCurrentEventId(eventResponse.data.currenteventid);
        } else {
          setCurrentEventId(null); // Will fallback to 3 in database insert
        }
      }

      // Load user's race official ID using getUserRole function
      if (user?.email) {
        const roleResponse = await getUserRole(user.email);
        if (roleResponse.success && roleResponse.data) {
          setRaceoffroleid(roleResponse.data.roleid);
          setJudgestation(roleResponse.data.judgestation);
        } else {
          setRaceoffroleid(null); // Will fallback to 1 in database insert
          setJudgestation(null);
        }
      }
      
      setIsLoading(false);
    };

    loadData();
  }, [user?.email]);

  // Handle judgestation-based location selection for roleid = 4
  useEffect(() => {
    if (raceoffroleid === 4 && judgestation !== null && locations.length > 0) {
      const judgeStationLocation = locations.find(loc => loc.locationid === judgestation);
      if (judgeStationLocation) {
        const judgeStationLocationId = judgeStationLocation.locationid.toString();
        setSelectedLocation(judgeStationLocationId);
        sessionStorage.setItem('currentLocation', judgeStationLocationId);
        setPenaltyTimeMultiplier(getDefaultMultiplierForLocation(judgeStationLocationId, locations));
      }
    }
  }, [raceoffroleid, judgestation, locations]);

  const handleQRScan = (result: string) => {
    console.log('QR Code scanned:', result);
    
    // Try to extract raceNumber and athleteName from URL query parameters
    try {
      const url = new URL(result);
      const raceNumberParam = url.searchParams.get('raceNumber');
      const athleteNameParam = url.searchParams.get('athleteName');
      
      if (raceNumberParam) {
        setRaceNumber(raceNumberParam);
        //alert(`Race Number ${raceNumberParam} extracted from QR code`);
      } else {
        // Fallback: if the scanned data looks like a valid alphanumeric race number (5 chars + 2 digits)
        if (/^[A-Za-z]{5}\d{2}$/.test(result)) {
          setRaceNumber(result);
          //alert(`Race Number ${result} detected from QR code`);
        } else {
          alert(`QR Code scanned but no valid race number found: ${result}`);
        }
      }
      
      // Set athlete name for display in green box
      if (athleteNameParam) {
        setAthleteName(athleteNameParam);
      } else {
        setAthleteName(null);
      }
    } catch (error) {
      // If it's not a valid URL, try the fallback approach
      if (/^[A-Za-z]{5}\d{2}$/.test(result)) {
        setRaceNumber(result);
        //alert(`Race Number ${result} detected from QR code`);
      } else {
        alert(`QR Code scanned: ${result}`);
      }
      setAthleteName(null);
    }
  };

  const handlePhotoCapture = (photoDataUrl: string) => {
    setCapturedPhoto(photoDataUrl);
    setShowCamera(false);
  };

  const handleRemovePhoto = () => {
    setCapturedPhoto(null);
  };

  // Penalty time modal handlers
  const handlePenaltyTimeClick = () => {
    setShowPenaltyTimeModal(true);
  };

  const handleMultiplierSelect = (multiplier: number) => {
    setPenaltyTimeMultiplier(multiplier);
    setShowPenaltyTimeModal(false);
  };

  const closePenaltyTimeModal = () => {
    setShowPenaltyTimeModal(false);
  };

  // Calculate the actual penalty time from 15s steps (not station base × multiplier)
  const getActualPenaltyTime = () => {
    return PENALTY_STEP_SECONDS * penaltyTimeMultiplier;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!selectedLocation || !user?.email) {
      setSubmitMessage("Please fill in all required fields. Make sure you are logged in with a valid email.");
      return;
    }

    // Check if either race number is provided OR a photo is captured
    if (!raceNumber && !capturedPhoto) {
      setSubmitMessage("Please enter a Race Number or capture a photo of the race number.");
      return;
    }

    // Get the actual penalty time (base time * multiplier)
    const actualPenaltyTime = getActualPenaltyTime();
    
    // Create notes string with penalty multiplier information
    const baseTime = locations.find(loc => loc.locationid.toString() === selectedLocation)?.penaltytime || 15;
    const notes = penaltyTimeMultiplier > Math.max(1, Math.round(baseTime / PENALTY_STEP_SECONDS))
      ? `Penalty x ${penaltyTimeMultiplier} (Step: ${PENALTY_STEP_SECONDS}s, Applied: ${actualPenaltyTime}s)`
      : undefined;

    // Find the corresponding penalty ID based on the actual penalty time
    const penaltyType = penaltyTypes.find(pt => pt.penaltytime === baseTime);
    const penaltyId = penaltyType?.penaltyid || 1; // Default to penalty ID 1 if not found

    setIsSubmitting(true);
    setSubmitMessage("");

    const submissionData: PenaltySubmissionData = {
      locationId: parseInt(selectedLocation),
      raceNumber: raceNumber || `Photo Captured`,
      penaltyId: penaltyId,
      penaltyTime: actualPenaltyTime,
      judgeEmail: user?.email || '',
      numPenalties: numPenalties ? parseInt(numPenalties) : 1,
      photoUrl: capturedPhoto || undefined,
      eventid: currentEventId || 7, // Use user's current event ID or fallback to 7
      raceoffroleid: raceoffroleid || 4, // Use user's race official ID or fallback to 5
      notes: notes
    };

    const result = await submitPenalty(submissionData);
    
    if (result.success) {
      const displayRaceNumber = raceNumber || "Photo Captured";
      setSubmitMessage("Penalty submitted for #" + displayRaceNumber);
      // Clear form fields but keep the success message
      setRaceNumber("");
      setNumPenalties("");
      setCapturedPhoto(null);
      setAthleteName(null);
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setSubmitMessage("");
      }, 2000);
    } else {
      setSubmitMessage(`Error: ${result.error}`);
    }
    
    setIsSubmitting(false);
  };

  const handleClear = () => {
    setRaceNumber("");
    setNumPenalties("");
    setSubmitMessage("");
    setCapturedPhoto(null);
    setAthleteName(null);
    // Don't clear location as it should persist during the session
  };

  return (
    <div className="pen-screen font-brutal flex flex-col min-h-screen">
      <Header 
        locations={locations}
        selectedLocation={selectedLocation}
        onLocationChange={handleLocationChange}
        isLoading={isLoading}
      />
      <div className="px-4 py-4 flex flex-col gap-3 max-w-lg mx-auto w-full flex-1">
        <QRScanner
          onScan={handleQRScan}
          stationName={
            locations.find((loc) => loc.locationid.toString() === selectedLocation)?.locationname ||
            undefined
          }
        />

        {isLoading && (
          <div className="text-center text-sw-muted text-xs uppercase tracking-wide font-extrabold font-brutal">
            Loading...
          </div>
        )}

        {athleteName && (
          <div className="bg-[#0e2a16] border-2 border-[rgba(48,176,53,0.35)] text-[#c7f0cb] px-3 py-2.5 text-[13px] text-center font-brutal">
            Athlete: <span className="font-extrabold">{athleteName}</span>
          </div>
        )}

        {selectedLocation && locations.length > 0 && (
          <button
            type="button"
            className="w-full border-2 border-black bg-sw-yellow text-black px-4 py-3.5 flex items-center justify-between gap-3 text-left cursor-pointer"
            onClick={handlePenaltyTimeClick}
          >
            <span className="text-[11px] font-extrabold uppercase tracking-[0.06em] font-brutal leading-snug max-w-[56%]">
              Station Penalty time · tap to change
            </span>
            <span className="font-mono font-light text-[26px] leading-none tracking-tight tabular-nums shrink-0">
              {getActualPenaltyTime()} SEC
              {penaltyTimeMultiplier > 1 && (
                <span className="text-xs ml-2 font-brutal font-extrabold align-middle">
                  (×{penaltyTimeMultiplier})
                </span>
              )}
            </span>
          </button>
        )}

        <form className="flex flex-col gap-3 flex-1" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="raceNumber" className="pen-label">
              Race Number {!capturedPhoto && <span className="text-sw-red">*</span>}
            </label>
            <input
              id="raceNumber"
              placeholder={capturedPhoto ? "Enter Race Number (optional - photo captured)" : "Enter Race Number"}
              value={raceNumber}
              onChange={(e) => setRaceNumber(e.target.value)}
              className="pen-input !rounded-none font-brutal font-light"
              required={!capturedPhoto}
            />
          </div>

          <div className="flex items-center justify-between gap-3 border-2 border-dashed border-sw-line px-3.5 py-3 bg-black">
            <span className="text-[11px] text-sw-muted font-extrabold uppercase tracking-[0.06em] font-brutal leading-snug">
              Optional · Photo of Race Number
            </span>
            <div className="flex items-center gap-2">
              {capturedPhoto && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="w-[42px] h-[42px] border-2 border-sw-red bg-sw-nav grid place-items-center text-sw-red hover:bg-sw-red hover:text-black"
                  title="Remove Photo"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                className={`w-[42px] h-[42px] border-2 grid place-items-center bg-sw-nav ${
                  capturedPhoto
                    ? 'border-sw-green text-sw-green'
                    : 'border-sw-border text-sw-muted hover:border-sw-yellow hover:text-sw-yellow'
                }`}
                title={capturedPhoto ? "Photo Captured - Click to Retake" : "Take Photo"}
              >
                <Camera className="w-5 h-5" />
              </button>
            </div>
          </div>

          {capturedPhoto && (
            <img
              src={capturedPhoto}
              alt="Captured race number"
              className="w-full h-32 object-cover border-2 border-sw-border"
            />
          )}

          {submitMessage && (
            <div
              className={`${
                submitMessage.includes('Error') ? 'pen-error' : 'pen-success'
              } !rounded-none font-brutal`}
            >
              {submitMessage}
            </div>
          )}

          <div className="flex gap-2.5 my-5">
            <button
              type="submit"
              className="pen-btn-primary flex-1 !rounded-none !text-[15px] font-brutal font-black"
              disabled={isSubmitting || isLoading || !selectedLocation}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="pen-btn-ghost flex-1 !rounded-none !text-[15px] font-brutal font-black !border-2 !border-sw-border !bg-transparent hover:!bg-white hover:!text-black"
              disabled={isSubmitting}
            >
              Clear
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="pen-btn-ghost w-full !rounded-none !text-[15px] font-brutal font-black !border-2 !border-sw-border"
          >
            GO BACK <Home className="w-4 h-4" />
          </button>

          <div className="mt-auto pt-6 pb-2 flex justify-center">
            <button
              type="button"
              onClick={() => navigate('/mark-dnf')}
              className="pen-btn-orange w-full !rounded-none !text-[15px] font-brutal font-black"
            >
              <X className="h-4 w-4" />
              Mark DNF
            </button>
          </div>
        </form>

        {showCamera && (
          <CameraCapture
            onCapture={handlePhotoCapture}
            onClose={() => setShowCamera(false)}
          />
        )}

        {showPenaltyTimeModal && (
          <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
            <div className="bg-sw-panel border-2 border-sw-yellow p-4 max-w-md w-full mx-4 font-brutal">
              <div className="flex justify-between items-center mb-2.5">
                <h3 className="text-sm font-black uppercase text-white tracking-[0.02em]">
                  Select Penalty Time Multiplier
                </h3>
                <button
                  onClick={closePenaltyTimeModal}
                  className="p-1 text-sw-muted hover:text-sw-yellow"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="pen-note !rounded-none mb-3 font-brutal">
                Base Penalty Time:{' '}
                <b className="text-[#c6c6c6] uppercase text-[11px] tracking-wide font-brutal">
                  {locations.find(loc => loc.locationid.toString() === selectedLocation)?.penaltytime || 15} seconds
                </b>
              </div>

              <div className="grid grid-cols-3 gap-2 my-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((multiplier) => {
                  const totalTime = PENALTY_STEP_SECONDS * multiplier;
                  const isSelected = penaltyTimeMultiplier === multiplier;

                  return (
                    <button
                      key={multiplier}
                      type="button"
                      onClick={() => handleMultiplierSelect(multiplier)}
                      className={`px-1.5 py-3 border-2 text-center ${
                        isSelected
                          ? 'border-sw-yellow bg-sw-yellow text-black'
                          : 'border-sw-border bg-black text-white'
                      }`}
                    >
                      <div className={`text-base font-black font-brutal ${isSelected ? 'text-black' : 'text-white'}`}>
                        ×{multiplier}
                      </div>
                      <div className={`font-mono text-[11px] mt-1 font-light ${isSelected ? 'text-[#5a4a00]' : 'text-sw-muted'}`}>
                        {totalTime}s
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={closePenaltyTimeModal}
                  className="pen-btn-ghost flex-1 !rounded-none font-brutal font-black"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleMultiplierSelect(penaltyTimeMultiplier)}
                  className="pen-btn-primary flex-1 !rounded-none font-brutal font-black"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}